import type { CouponCampaignKind, CouponPrintOverlayLayout } from '../types';

export const DEFAULT_COUPON_PRINT_OVERLAY: CouponPrintOverlayLayout = {
  codeLeftPct: 50,
  codeTopPct: 56,
  offLeftPct: 50,
  offTopPct: 64,
  expiryLeftPct: 50,
  expiryTopPct: 74,
  textColor: '#ffffff',
  codeFontRem: 0.95,
  offFontRem: 1.72,
  expiryFontRem: 0.68,
  barcodeLeftPct: 50,
  barcodeTopPct: 88,
  barcodeWidthPct: 62,
  barcodeHeightPct: 12,
  barcodeRotateDeg: 0,
  conditionsLeftPct: 50,
  conditionsTopPct: 92,
  conditionsFontRem: 0.48,
  conditionsWidthPct: 88,
  conditionsRotateDeg: 0,
  unitLabelLeftPct: 50,
  unitLabelTopPct: 42,
  unitLabelFontRem: 1.35,
};

/** Layout tuned for building unit cards (unit number prominent). */
export const DEFAULT_BUILDING_UNIT_PRINT_OVERLAY: CouponPrintOverlayLayout = {
  unitLabelLeftPct: 50,
  unitLabelTopPct: 26,
  unitLabelFontRem: 1.65,
  codeLeftPct: 50,
  codeTopPct: 54,
  offLeftPct: 50,
  offTopPct: 62,
  expiryLeftPct: 50,
  expiryTopPct: 70,
  textColor: '#0f172a',
  codeFontRem: 0.72,
  offFontRem: 1.55,
  expiryFontRem: 0.58,
  barcodeLeftPct: 50,
  barcodeTopPct: 86,
  barcodeWidthPct: 58,
  barcodeHeightPct: 11,
  barcodeRotateDeg: 0,
  conditionsLeftPct: 50,
  conditionsTopPct: 78,
  conditionsFontRem: 0.42,
  conditionsWidthPct: 86,
  conditionsRotateDeg: 0,
};

export function defaultPrintOverlayForKind(
  kind: CouponCampaignKind = 'promotional'
): CouponPrintOverlayLayout {
  return kind === 'building_unit'
    ? { ...DEFAULT_BUILDING_UNIT_PRINT_OVERLAY }
    : { ...DEFAULT_COUPON_PRINT_OVERLAY };
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 50;
  return Math.min(94, Math.max(4, n));
}

/** Finer 0.1% steps for barcode / unit label alignment. */
function clampPctFine(n: number): number {
  if (!Number.isFinite(n)) return 50;
  const clamped = Math.min(94, Math.max(4, n));
  return Math.round(clamped * 10) / 10;
}

/** Snap drag position: coarse (1%) for text, fine (0.1%) for barcode & unit. */
export function snapOverlayDragPct(pct: number, mode: 'coarse' | 'fine'): number {
  return mode === 'fine' ? clampPctFine(pct) : Math.round(clampPct(pct));
}

function clampSizePct(n: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function clampRotateDeg(n: number | undefined): number {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return 0;
  return Math.max(-180, Math.min(180, Math.round(v)));
}

export function clampFontRem(n: number | undefined, fallback: number): number {
  const v = n ?? fallback;
  if (!Number.isFinite(v)) return fallback;
  return Math.min(4.5, Math.max(0.4, v));
}

export function sanitizeCssColor(color: string | undefined, fallback: string): string {
  if (!color || typeof color !== 'string') return fallback;
  const t = color.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(t)) return t;
  return fallback;
}

/** Whether an overlay field is shown (undefined = visible). */
export function isPrintOverlayVisible(
  overlay: CouponPrintOverlayLayout,
  field: 'unitLabel' | 'code' | 'discount' | 'expiry' | 'barcode' | 'conditions'
): boolean {
  switch (field) {
    case 'unitLabel':
      return overlay.showUnitLabel !== false;
    case 'code':
      return overlay.showCode !== false;
    case 'discount':
      return overlay.showDiscount !== false;
    case 'expiry':
      return overlay.showExpiry !== false;
    case 'barcode':
      return overlay.showBarcode !== false;
    case 'conditions':
      return overlay.showConditions !== false;
    default:
      return true;
  }
}

export function mergeCouponPrintOverlay(
  partial: Partial<CouponPrintOverlayLayout> | null | undefined,
  campaignKind: CouponCampaignKind = 'promotional'
): CouponPrintOverlayLayout {
  const base = defaultPrintOverlayForKind(campaignKind);
  if (!partial) return base;
  return {
    ...base,
    ...partial,
    codeLeftPct: clampPct(partial.codeLeftPct ?? base.codeLeftPct),
    codeTopPct: clampPct(partial.codeTopPct ?? base.codeTopPct),
    offLeftPct: clampPct(partial.offLeftPct ?? base.offLeftPct),
    offTopPct: clampPct(partial.offTopPct ?? base.offTopPct),
    expiryLeftPct: clampPct(partial.expiryLeftPct ?? base.expiryLeftPct),
    expiryTopPct: clampPct(partial.expiryTopPct ?? base.expiryTopPct),
    textColor: partial.textColor?.trim() || base.textColor,
    codeFontRem: clampFontRem(partial.codeFontRem, base.codeFontRem!),
    offFontRem: clampFontRem(partial.offFontRem, base.offFontRem!),
    expiryFontRem: clampFontRem(partial.expiryFontRem, base.expiryFontRem!),
    barcodeLeftPct: clampPctFine(partial.barcodeLeftPct ?? base.barcodeLeftPct!),
    barcodeTopPct: clampPctFine(partial.barcodeTopPct ?? base.barcodeTopPct!),
    barcodeWidthPct: clampSizePct(partial.barcodeWidthPct ?? base.barcodeWidthPct!, 20, 95, base.barcodeWidthPct!),
    barcodeHeightPct: clampSizePct(partial.barcodeHeightPct ?? base.barcodeHeightPct!, 6, 30, base.barcodeHeightPct!),
    barcodeRotateDeg: clampRotateDeg(partial.barcodeRotateDeg ?? base.barcodeRotateDeg),
    conditionsLeftPct: clampPct(partial.conditionsLeftPct ?? base.conditionsLeftPct!),
    conditionsTopPct: clampPct(partial.conditionsTopPct ?? base.conditionsTopPct!),
    conditionsFontRem: clampFontRem(partial.conditionsFontRem, base.conditionsFontRem!),
    conditionsWidthPct: clampSizePct(partial.conditionsWidthPct ?? base.conditionsWidthPct!, 30, 96, base.conditionsWidthPct!),
    conditionsRotateDeg: clampRotateDeg(partial.conditionsRotateDeg ?? base.conditionsRotateDeg),
    unitLabelLeftPct: clampPctFine(partial.unitLabelLeftPct ?? base.unitLabelLeftPct!),
    unitLabelTopPct: clampPctFine(partial.unitLabelTopPct ?? base.unitLabelTopPct!),
    unitLabelFontRem: clampFontRem(partial.unitLabelFontRem, base.unitLabelFontRem!),
  };
}
